import { useState, useEffect } from 'react'
import Modal from '../shared/Modal'
import { MarketCustomColumn } from '../../types/database'

export interface ExportOptions {
  pricingMode: 'internal' | 'external'
  preparedFor?: string
  historicalMonths: number // Number of months of historical data for sold/removed sections
  hideOffMarket?: boolean // Hide off-market aircraft from the PDF
  currentMarketOnly?: boolean // Only include "Currently on Market" section, exclude Sold and Removed
  hideRemovedFromMarket?: boolean // Hide "Removed from Market" section, show Current Market and Sold
  marketOverview: 'include' | 'exclude' | 'only' // Market Overview page visibility
  columns: {
    serialNumber: boolean
    owner: boolean
    broker: boolean
    yearMfrDlv: boolean
    aftt: boolean
    emp: boolean
    damage: boolean
    pax: boolean
    paintInterior: boolean
    options: boolean
    relativeValue: boolean
    dateListed: boolean
    askingPrice: boolean
    takePrice: boolean
    custom1: boolean
    custom2: boolean
    custom3: boolean
  }
}

interface ExportOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  onContinue: (options: ExportOptions) => Promise<void> | void
  customColumns?: MarketCustomColumn[]
  isLoading?: boolean
  /** Initial value for historical months - synced from user preferences */
  initialHistoricalMonths?: number
}

export default function ExportOptionsModal({
  isOpen,
  onClose,
  onContinue,
  customColumns = [],
  isLoading = false,
  initialHistoricalMonths = 12
}: ExportOptionsModalProps) {
  const [pricingMode, setPricingMode] = useState<'internal' | 'external'>('internal')
  const [preparedFor, setPreparedFor] = useState('')
  const [historicalMonths, setHistoricalMonths] = useState(initialHistoricalMonths)
  const [hideOffMarket, setHideOffMarket] = useState(false)
  // Section visibility: 'all' | 'currentOnly' | 'hideRemoved'
  const [sectionVisibility, setSectionVisibility] = useState<'all' | 'currentOnly' | 'hideRemoved'>('all')
  // Market Overview page visibility: 'include' | 'exclude' | 'only'
  const [marketOverview, setMarketOverview] = useState<'include' | 'exclude' | 'only'>('include')
  const [isGenerating, setIsGenerating] = useState(false)
  const [columns, setColumns] = useState({
    serialNumber: true,
    owner: true,
    broker: true,
    yearMfrDlv: true,
    aftt: true,
    emp: true,
    damage: true,
    pax: true,
    paintInterior: true,
    options: true,
    relativeValue: true,
    dateListed: true,
    askingPrice: true,
    takePrice: true,
    custom1: true,
    custom2: true,
    custom3: true
  })

  // Sync historicalMonths with prop only when modal opens (not while open)
  // This allows the user to adjust the value within the modal without it being overwritten
  useEffect(() => {
    if (isOpen) {
      setHistoricalMonths(initialHistoricalMonths)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]) // Intentionally only depend on isOpen, not initialHistoricalMonths

  const handleColumnToggle = (columnKey: keyof typeof columns) => {
    setColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }))
  }

  const handleSelectAll = () => {
    const allSelected = Object.values(columns).every(Boolean)
    const newValue = !allSelected
    setColumns({
      serialNumber: newValue,
      owner: newValue,
      broker: newValue,
      yearMfrDlv: newValue,
      aftt: newValue,
      emp: newValue,
      damage: newValue,
      pax: newValue,
      paintInterior: newValue,
      options: newValue,
      relativeValue: newValue,
      dateListed: newValue,
      askingPrice: newValue,
      takePrice: newValue,
      custom1: newValue,
      custom2: newValue,
      custom3: newValue
    })
  }

  const handleContinue = async () => {
    setIsGenerating(true)
    try {
      await onContinue({
        pricingMode,
        preparedFor,
        historicalMonths,
        hideOffMarket,
        currentMarketOnly: sectionVisibility === 'currentOnly',
        hideRemovedFromMarket: sectionVisibility === 'hideRemoved',
        marketOverview,
        columns
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const handleClose = () => {
    if (!isGenerating && !isLoading) {
      onClose()
    }
  }

  const showLoading = isGenerating || isLoading

  const allSelected = Object.values(columns).every(Boolean)
  const someSelected = Object.values(columns).some(Boolean)

  // Full-screen loading overlay for preview generation
  if (showLoading) {
    return (
      <Modal isOpen={isOpen} onClose={() => {}} title="Export Market Summary as PDF" size="medium">
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="text-center">
            <div className="inline-flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg text-gray-600">Generating Preview...</span>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Export Market Summary as PDF" size="medium">
      <div className="space-y-6">
        {/* Prepared For Field */}
        <div>
          <label htmlFor="preparedFor" className="block text-sm font-medium text-gray-900 mb-2">
            Prepared For
          </label>
          <input
            type="text"
            id="preparedFor"
            value={preparedFor}
            onChange={(e) => setPreparedFor(e.target.value)}
            placeholder="Enter client name or organization"
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            This will appear on the header page of the PDF report
          </p>
        </div>

        {/* Historical Data Range */}
        <div>
          <label htmlFor="historicalMonths" className="block text-sm font-medium text-gray-900 mb-2">
            Historical Data Range
          </label>
          <div className="flex items-center space-x-3">
            <input
              type="range"
              id="historicalMonths"
              value={historicalMonths}
              onChange={(e) => setHistoricalMonths(parseInt(e.target.value, 10))}
              min="1"
              max="48"
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
            />
            <span className="text-sm text-gray-700 min-w-[5rem] text-right">
              {historicalMonths} {historicalMonths === 1 ? 'month' : 'months'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Applies to Recently Sold and Removed from Market sections only
          </p>
        </div>

        {/* Pricing Mode Selector */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Pricing Mode</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="pricingMode"
                value="internal"
                checked={pricingMode === 'internal'}
                onChange={(e) => setPricingMode(e.target.value as 'internal' | 'external')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Actual Ask / Take</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="pricingMode"
                value="external"
                checked={pricingMode === 'external'}
                onChange={(e) => setPricingMode(e.target.value as 'internal' | 'external')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Adjusted Ask / Take</span>
              </span>
            </label>
          </div>
        </div>

        {/* Hide Off-Market Aircraft */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={hideOffMarket}
              onChange={(e) => setHideOffMarket(e.target.checked)}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <span className="ml-2 text-sm text-gray-700">
              <span className="font-semibold">Hide Off-Market Aircraft</span>
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500 ml-6">
            Exclude off-market aircraft from the PDF report
          </p>
        </div>

        {/* Section Visibility */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Sections to Include</h4>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="sectionVisibility"
                checked={sectionVisibility === 'all'}
                onChange={() => setSectionVisibility('all')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">All Sections</span>
                <span className="text-gray-500 ml-1">- Current Market, Sold, and Removed</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="sectionVisibility"
                checked={sectionVisibility === 'hideRemoved'}
                onChange={() => setSectionVisibility('hideRemoved')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Hide Removed from Market</span>
                <span className="text-gray-500 ml-1">- Current Market and Sold only</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="sectionVisibility"
                checked={sectionVisibility === 'currentOnly'}
                onChange={() => setSectionVisibility('currentOnly')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Current Market Only</span>
                <span className="text-gray-500 ml-1">- Exclude Sold and Removed</span>
              </span>
            </label>
          </div>
        </div>

        {/* Market Overview Page */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Market Overview Page</h4>
          <p className="text-xs text-gray-500 mb-2">
            Includes market statistics: average sale price, number of sales, and time on market
          </p>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="marketOverview"
                checked={marketOverview === 'include'}
                onChange={() => setMarketOverview('include')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Include with Report</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="marketOverview"
                checked={marketOverview === 'exclude'}
                onChange={() => setMarketOverview('exclude')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Exclude from Report</span>
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="marketOverview"
                checked={marketOverview === 'only'}
                onChange={() => setMarketOverview('only')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">
                <span className="font-semibold">Print Only Market Overview</span>
                <span className="text-gray-500 ml-1">- Cover + Market Overview pages only</span>
              </span>
            </label>
          </div>
        </div>

        {/* Column Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900">Columns to Include</h4>
            <button
              type="button"
              onClick={handleSelectAll}

              className="text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-400"
            >
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.serialNumber}
                onChange={() => handleColumnToggle('serialNumber')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Serial Number</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.owner}
                onChange={() => handleColumnToggle('owner')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Owner</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.broker}
                onChange={() => handleColumnToggle('broker')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Broker</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.yearMfrDlv}
                onChange={() => handleColumnToggle('yearMfrDlv')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Manufactured / Delivered Year</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.aftt}
                onChange={() => handleColumnToggle('aftt')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">AFTT</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.emp}
                onChange={() => handleColumnToggle('emp')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">EMP</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.damage}
                onChange={() => handleColumnToggle('damage')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">DMG</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.pax}
                onChange={() => handleColumnToggle('pax')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">PAX</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.paintInterior}
                onChange={() => handleColumnToggle('paintInterior')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Paint / Interior</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.options}
                onChange={() => handleColumnToggle('options')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Options</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.relativeValue}
                onChange={() => handleColumnToggle('relativeValue')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Comparative Values</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.dateListed}
                onChange={() => handleColumnToggle('dateListed')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Date Listed</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.askingPrice}
                onChange={() => handleColumnToggle('askingPrice')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Asking Price</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={columns.takePrice}
                onChange={() => handleColumnToggle('takePrice')}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Take Price</span>
            </label>
          </div>
        </div>

        {/* Custom Columns */}
        {customColumns.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Custom Columns</h4>
            <div className="grid grid-cols-2 gap-3">
              {customColumns.map((column) => (
                <div key={column.id}>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={columns[`custom${column.column_index}` as keyof typeof columns]}
                      onChange={() => handleColumnToggle(`custom${column.column_index}` as keyof typeof columns)}

                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{column.column_title}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={showLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleContinue}
            disabled={!someSelected}
            className="px-6 py-2 text-sm font-medium text-white bg-purple-700 border border-transparent rounded-md hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            <span>Continue</span>
          </button>
        </div>
      </div>
    </Modal>
  )
}
